/**
 * Poseidon dashboard — application services.
 *
 * One place decides whether the UI is talking to the Rust daemon or to
 * `src/mock/`. There is no automatic fallback in either direction: live mode
 * never silently becomes mock, and mock mode never pretends to be live.
 *
 * This provider also owns:
 *   - the operator session and the in-memory CSRF token (§3.1, §16.4.1);
 *   - the dashboard-wide WebSocket subscription registry used by pages to apply
 *     deltas without each page opening its own socket.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createLiveApi, newIdempotencyKey, setCsrfToken, setUnauthorizedHandler } from '../api/client';
import type { DashboardApi } from '../api/client';
import { useDashboardSocket } from '../api/useDashboardSocket';
import type { DashboardSocketState } from '../api/useDashboardSocket';
import type {
  AuthSessionView,
  CommandKind,
  CommandReceipt,
  DashboardEvent,
  DashboardEventType,
  DataSourceMode,
} from '../api/types';
import { API_MODE, IS_MOCK_MODE } from '../config';
import { createMockApi } from '../mock/mockApi';
import { useMockSocket } from '../mock/useMockSocket';

export interface SendCommandOptions {
  /** The exact phrase the operator typed; the daemon verifies it independently. */
  confirmation: string;
  reason?: string | null;
}

export interface Services {
  api: DashboardApi;
  mode: DataSourceMode;
  isMock: boolean;
  socketStatus: DashboardSocketState['status'];
  socket: DashboardSocketState & { reconnect: () => void };
  session: AuthSessionView | null;
  sessionLoading: boolean;
  sessionError: string | null;
  login: (operator: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Bumped when the socket detects a sequence gap, so pages refetch snapshots. */
  resyncToken: number;
  sendCommand: (command: CommandKind, payload: unknown, options: SendCommandOptions) => Promise<CommandReceipt>;
  subscribe: (listener: (event: DashboardEvent) => void) => () => void;
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ children }: { children: ReactNode }): JSX.Element {
  const mode = API_MODE;
  const isMock = IS_MOCK_MODE;

  const api = useMemo<DashboardApi>(() => (isMock ? createMockApi() : createLiveApi()), [isMock]);

  const [session, setSession] = useState<AuthSessionView | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const listenersRef = useRef(new Set<(event: DashboardEvent) => void>());

  const subscribe = useCallback((listener: (event: DashboardEvent) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const handleEvent = useCallback((event: DashboardEvent) => {
    for (const listener of listenersRef.current) listener(event);
  }, []);

  const liveSocket = useDashboardSocket({ enabled: !isMock, onEvent: handleEvent });
  const mockSocket = useMockSocket({ enabled: isMock, onEvent: handleEvent });
  const socket = isMock ? mockSocket : liveSocket;

  // A 401 from any request drops the operator back to the login screen.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession((prev) => (prev === null ? prev : { ...prev, authenticated: false, csrf_token: null }));
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const bootstrap = useCallback(async () => {
    setSessionLoading(true);
    try {
      const result = await api.getSession();
      setSession(result.data);
      setCsrfToken(result.data.authenticated ? result.data.csrf_token : null);
      setSessionError(null);
    } catch (error) {
      setSession(null);
      setCsrfToken(null);
      setSessionError(error instanceof Error ? error.message : 'Could not establish an operator session.');
    } finally {
      setSessionLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const login = useCallback(
    async (operator: string, password: string) => {
      setSessionLoading(true);
      try {
        const result = await api.login(operator, password);
        setSession(result.data);
        setCsrfToken(result.data.authenticated ? result.data.csrf_token : null);
        setSessionError(null);
      } catch (error) {
        setSessionError(error instanceof Error ? error.message : 'Sign-in failed.');
        throw error;
      } finally {
        setSessionLoading(false);
      }
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setSession(null);
      setCsrfToken(null);
    }
  }, [api]);

  const sendCommand = useCallback(
    async (command: CommandKind, payload: unknown, options: SendCommandOptions): Promise<CommandReceipt> => {
      const result = await api.sendCommand({
        command,
        payload,
        confirmation: options.confirmation,
        reason: options.reason ?? null,
        idempotency_key: newIdempotencyKey(),
        requested_at: new Date().toISOString(),
      });
      return result.data;
    },
    [api],
  );

  const value = useMemo<Services>(
    () => ({
      api,
      mode,
      isMock,
      socketStatus: socket.status,
      socket,
      session,
      sessionLoading,
      sessionError,
      login,
      logout,
      resyncToken: socket.resyncToken,
      sendCommand,
      subscribe,
    }),
    [api, mode, isMock, socket, session, sessionLoading, sessionError, login, logout, sendCommand, subscribe],
  );

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const value = useContext(ServicesContext);
  if (value === null) {
    throw new Error('useServices must be used inside a ServicesProvider');
  }
  return value;
}

/**
 * Subscribe to one class of WebSocket frames. The handler is called only for the
 * requested type, already narrowed, so pages never hand-narrow payloads.
 */
export function useDashboardEvent<TType extends DashboardEventType>(
  type: TType,
  handler: (payload: Extract<DashboardEvent, { type: TType }>['payload']) => void,
): void {
  const { subscribe } = useServices();
  // Stored as a broad signature because `Extract<DashboardEvent, { type: TType }>`
  // cannot be resolved while `TType` is still generic. The runtime check below is
  // what makes the narrowing sound.
  const handlerRef = useRef<(payload: DashboardEvent['payload']) => void>(
    handler as (payload: DashboardEvent['payload']) => void,
  );

  useEffect(() => {
    handlerRef.current = handler as (payload: DashboardEvent['payload']) => void;
  }, [handler]);

  useEffect(() => {
    return subscribe((event) => {
      if (event.type !== type) return;
      handlerRef.current(event.payload);
    });
  }, [subscribe, type]);
}
